/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-10-11 22:41:00
 *
 *******************************************************/
// @flow

import _ from 'lodash';

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Page, Layout, Card, Button } from '@shopify/polaris';
import { getFormValues, reduxForm, FieldArray, reset, change } from 'redux-form';
import { withI18n } from '@shopify/react-i18n';

import { routes } from 'src/routes';

import { isEmptySynonyms, checkValidateSynonyms, buildSynonyms } from 'src/helpers/synonym-helpers';
import InputError from 'src/components/views/synonym/input-error';
import SynonymOnCreate from 'src/components/views/synonym/synonyms-on-create';
import ActionsGroupOnCreating from 'src/components/views/synonym/actions-group-on-creating';
import ExceededPlanNotification from 'src/components/containers/common/exceeded-plan-notifications';
import RouteLeavingGuard from 'src/components/views/common/handle-leave/route-leaving-guard';

import { confirmLeavePage, getShop } from 'src/helpers';
import * as synonymActions from 'src/stores/actions/synonym-actions';
import * as quickStartGuideAction from 'actions/dashboard/quick-start-guide-action';

import type { ComposedProps, State } from './new.type';
import Emitter, { EVENT_KEY } from 'src/libs/eventEmitter';
import { StandardSearchTab } from 'src/constants/search';
import history from 'src/routes/history';

class NewSynonym extends Component<ComposedProps, State> {
  state = {
    dismissedBanner: true,
    messageError: ''
  };

  componentDidMount() {
    try {
      const { dispatch, initialValues } = this.props;
      const synonyms = _.get(initialValues, 'synonyms', []);
      const searchTerm = localStorage.getItem(`${getShop()}-synonym-search-term`);
      if (searchTerm) {
        dispatch(
          change(
            'NewSynonymForm',
            'synonyms',
            synonyms.map((item, index) => {
              if (index === 0) {
                return {
                  ...item,
                  searchTerm
                };
              }
              return item;
            })
          )
        );
        localStorage.removeItem(`${getShop()}-synonym-search-term`);
      }
    } catch (error) {}

    // Handle when reload or leave page
    window.addEventListener('beforeunload', this.confirmLeavePage);
  }

  componentWillUnmount() {
    // Handle when reload or leave page
    window.removeEventListener('beforeunload', this.confirmLeavePage);
  }

  render() {
    const { i18n, dirty, handleSubmit, pristine, submitting, saving, formValues = {} } = this.props;
    const { dismissedBanner, messageError } = this.state;
    const emptySynonyms = formValues ? isEmptySynonyms(formValues['synonyms']) : true;

    return (
      <div className="BC-Page BC-Page__Synonym">
        <Page
          title={i18n.translate('AdminHTML.Synonym.Add.title')}
          breadcrumbs={[
            {
              content: i18n.translate('AdminHTML.Synonym.Synonym.title'),
              onAction: () => this.backToManagementPage()
            }
          ]}
          primaryAction={
            <div className="BC-Page BC-Page__Button BC-Page__Button-HasBreadcrumb">
              <ActionsGroupOnCreating
                handleSubmit={handleSubmit(this.handleSave.bind(this))}
                discardChange={() => this.backToManagementPage()}
                pristine={pristine}
                submitting={submitting}
                saving={saving}
                emptySynonyms={emptySynonyms}
              />
            </div>
          }>
          <Layout>
            <Layout.Section>
              <div className="BC-Container__Notifications">
                {/* Sticky notification for exceeded plan */}
                <ExceededPlanNotification />

                {/* Banner error */}
                {!dismissedBanner && (
                  <InputError onDismiss={this.handleDismissBanner} message={messageError} />
                )}
              </div>
            </Layout.Section>
            {/* List of synonym */}
            <Layout.Section>
              <Card sectioned>
                <Card.Section>
                  <form onSubmit={this.handleDefaultSubmit} className="newSynonyms">
                    <FieldArray name="synonyms" component={SynonymOnCreate} />
                  </form>
                </Card.Section>

                <Card.Section>
                  <div className="addMoreSynonyms">
                    <Button primary onClick={this.insertNewRow}>
                      {i18n.translate('AdminHTML.Common.Action.addSymbol')}
                    </Button>
                  </div>
                </Card.Section>
              </Card>

              <div className="pagination">
                <div className="actionsGroup">
                  <ActionsGroupOnCreating
                    handleSubmit={handleSubmit(this.handleSave.bind(this))}
                    discardChange={this.backToManagementPage}
                    pristine={pristine}
                    submitting={submitting}
                    saving={saving}
                    emptySynonyms={emptySynonyms}
                  />
                </div>
              </div>
            </Layout.Section>
          </Layout>
        </Page>

        {/* Confirm before leaving */}
        <RouteLeavingGuard
          when={dirty}
          // Navigate function
          navigate={path =>
            history.push({
              pathname: path,
              hash: StandardSearchTab.manualSynonyms
            })
          }
          shouldBlockNavigation={dirty}
        />
      </div>
    );
  }

  // Back to management synonyms
  backToManagementPage = () => {
    history.push(routes.synonym.view());
  };

  // Insert new row
  insertNewRow = () => {
    const { formValues, updateField } = this.props;
    let { synonyms = [] } = formValues;
    // Create new object different from the old value in form,
    // to make redux form know the change: pristine = false
    let synonymsClone = _.cloneDeep(synonyms);

    synonymsClone.push({
      searchTerm: '',
      biDirection: '<=>',
      synonyms: '',
      allowSuggestion: false
    });

    // Update synonym form
    updateField('synonyms', synonymsClone);
  };

  // Save synonyms
  handleDefaultSubmit = e => {
    e.preventDefault();
  };

  async handleSave(data) {
    const { i18n, resetForm, markDoneStep } = this.props;
    const { synonyms = [] } = data;

    // Get synonym having term
    let validSynonyms = _.filter(synonyms, synonym => {
      return !_.isEmpty(synonym.searchTerm) && !_.isEmpty(synonym.synonyms);
    });

    const validateSynonymsResult = checkValidateSynonyms(validSynonyms);
    if (!_.isEmpty(validateSynonymsResult)) {
      this.setState({
        dismissedBanner: false,
        messageError: i18n.translate(validateSynonymsResult)
      });
      return;
    }

    this.setState({ dismissedBanner: true, messageError: '' });

    // Save if having new terms
    // Build synonyms post package
    const saveSynonyms = buildSynonyms(validSynonyms);

    // Save to db
    const result = await this.props.bulkSynonyms(saveSynonyms);

    const { type } = result;
    if (type === 'BULK_SYNONYMS_SUCCESS') {
      setTimeout(() => {
        resetForm();
        this.backToManagementPage();
      }, 500);
    }

    markDoneStep('V2_SEARCH1', {
      isDone: true
    });

    Emitter.emit(EVENT_KEY.RELOAD_CSAT_ADMIN_APP_DISPLAY);
  }

  // Handle hide banner
  handleDismissBanner = () => {
    // Hide banner
    this.setState({ dismissedBanner: true, messageError: '' });
  };

  // Handle confirm leave page
  confirmLeavePage = event => {
    const { dirty, i18n } = this.props;
    return confirmLeavePage(event, dirty, i18n);
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  // Update redux-form field
  updateField: (field, value) => dispatch(change(`NewSynonymForm`, field, value)),
  bulkSynonyms: synonyms => dispatch(synonymActions.bulkSynonyms(synonyms, ownProps.i18n)),
  resetForm: () => dispatch(reset('NewSynonymForm')),
  markDoneStep: (stepName, value) =>
    dispatch(quickStartGuideAction.markDoneStep(stepName, value, ownProps.i18n))
});

const mapStateToProps = (state, props) => {
  const formValues = getFormValues('NewSynonymForm')(state);
  const total = _.get(state.synonym, 'total', 0);
  const synonyms = [...Array(3).keys()].map((item, key) => {
    return {
      searchTerm: '',
      biDirection: '<=>',
      synonyms: '',
      allowSuggestion: false
    };
  });

  return {
    formValues,
    initialValues: {
      synonyms
    },
    saving: _.get(state.synonym, 'saving', false),
    success: _.get(state.synonym, 'success', false),
    total
  };
};

const NewSynonymForm = reduxForm({
  form: 'NewSynonymForm', // a unique identifier for this form
  enableReinitialize: true
})(NewSynonym);

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(NewSynonymForm));
